import { useState, useEffect } from "react";
import { PartyProfile } from "@/data/partiesStore";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Save, Plus, Trash2, Link as LinkIcon, Building2, BookOpen, Users, Network } from "lucide-react";

interface AdminPartyProfileEditorProps {
  initialProfile: PartyProfile;
  onSave: (profile: PartyProfile) => void;
  isSaving: boolean;
}

export default function AdminPartyProfileEditor({ initialProfile, onSave, isSaving }: AdminPartyProfileEditorProps) {
  const [profile, setProfile] = useState<PartyProfile>(initialProfile);

  useEffect(() => {
    setProfile(initialProfile);
  }, [initialProfile]);

  const handleChange = (field: string, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialChange = (network: string, value: string) => {
    setProfile(prev => ({
      ...prev,
      socials: { ...prev.socials, [network]: value }
    }));
  };

  const handlePrincipleChange = (index: number, field: string, value: string) => {
    const newPrinciples = [...(profile.partyPrinciples || [])];
    newPrinciples[index] = { ...newPrinciples[index], [field]: value };
    handleChange('partyPrinciples', newPrinciples);
  };

  const addPrinciple = () => {
    const newPrinciples = [...(profile.partyPrinciples || []), { title: "", description: "", readMoreUrl: "" }];
    handleChange('partyPrinciples', newPrinciples);
  };

  const removePrinciple = (index: number) => {
    const newPrinciples = [...(profile.partyPrinciples || [])];
    newPrinciples.splice(index, 1);
    handleChange('partyPrinciples', newPrinciples);
  };

  const handleSave = (publish: boolean) => {
    onSave({ ...profile, status: publish ? 'valide' : (profile.status === 'valide' ? 'valide' : 'en_attente') });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Default to publishing if enter is pressed
    handleSave(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in duration-300">
      
      {/* 1. Informations Générales */}
      <Card className="border-border shadow-sm">
        <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Informations Générales</h3>
        </div>
        <CardContent className="p-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Nom Complet (Français)</Label>
            <Input 
              value={profile.fullNameFr || ""} 
              onChange={e => handleChange('fullNameFr', e.target.value)} 
              placeholder="Ex: Parti de l'Istiqlal"
            />
          </div>
          <div className="space-y-2">
            <Label>Nom Complet (Arabe)</Label>
            <Input 
              value={profile.fullNameAr || ""} 
              onChange={e => handleChange('fullNameAr', e.target.value)} 
              placeholder="Ex: حزب الاستقلال"
              dir="rtl"
            />
          </div>
          <div className="space-y-2">
            <Label>Nom Complet (Amazigh)</Label>
            <Input 
              value={profile.fullNameMz || ""} 
              onChange={e => handleChange('fullNameMz', e.target.value)} 
              placeholder="Ex: ⴰⵎⵓⵍⵍⵉ ⵏ ⵍⵉⵙⵜⵉⵇⵍⴰⵍ"
              dir="auto"
            />
          </div>
          <div className="space-y-2">
            <Label>Axe Majeur / Slogan (Français)</Label>
            <Input 
              value={profile.focusFr || ""} 
              onChange={e => handleChange('focusFr', e.target.value)} 
              placeholder="Ex: Pour la dignité du citoyen"
            />
          </div>
          <div className="space-y-2">
            <Label>Axe Majeur / Slogan (Arabe)</Label>
            <Input 
              value={profile.focusAr || ""} 
              onChange={e => handleChange('focusAr', e.target.value)} 
              placeholder="Ex: من أجل كرامة المواطن"
              dir="rtl"
            />
          </div>
          <div className="space-y-2">
            <Label>Axe Majeur / Slogan (Amazigh)</Label>
            <Input 
              value={profile.focusMz || ""} 
              onChange={e => handleChange('focusMz', e.target.value)} 
              placeholder="Ex: ⵉ ⵡⴰⴷⴷⵓⵔ ⵏ ⵓⵏⴰⵎⵓⵔ"
              dir="auto"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Site Web Officiel</Label>
            <Input 
              value={profile.website || ""} 
              onChange={e => handleChange('website', e.target.value)} 
              placeholder="https://www.exemple.ma"
              type="url"
            />
          </div>
        </CardContent>
      </Card>

      {/* 2. Réseaux Sociaux */}
      <Card className="border-border shadow-sm">
        <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center gap-2">
          <Network className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Réseaux Sociaux</h3>
        </div>
        <CardContent className="p-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Lien Facebook</Label>
            <Input 
              value={profile.socials?.facebook || ""} 
              onChange={e => handleSocialChange('facebook', e.target.value)} 
              placeholder="https://facebook.com/..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Lien X (Twitter)</Label>
            <Input 
              value={profile.socials?.twitter || ""} 
              onChange={e => handleSocialChange('twitter', e.target.value)} 
              placeholder="https://x.com/..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Lien Instagram</Label>
            <Input 
              value={profile.socials?.instagram || ""} 
              onChange={e => handleSocialChange('instagram', e.target.value)} 
              placeholder="https://instagram.com/..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Lien YouTube</Label>
            <Input 
              value={profile.socials?.youtube || ""} 
              onChange={e => handleSocialChange('youtube', e.target.value)} 
              placeholder="https://youtube.com/..."
              type="url"
            />
          </div>
        </CardContent>
      </Card>

      {/* 3. Structures et Organisations */}
      <Card className="border-border shadow-sm">
        <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Structures et Organisations</h3>
        </div>
        <CardContent className="p-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label>Organisation des Jeunes (Lien)</Label>
            <Input 
              value={profile.jeunesseUrl || ""} 
              onChange={e => handleChange('jeunesseUrl', e.target.value)} 
              placeholder="https://..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Organisation des Femmes (Lien)</Label>
            <Input 
              value={profile.femmesUrl || ""} 
              onChange={e => handleChange('femmesUrl', e.target.value)} 
              placeholder="https://..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Académie / Fondation (Lien)</Label>
            <Input 
              value={profile.academieUrl || ""} 
              onChange={e => handleChange('academieUrl', e.target.value)} 
              placeholder="https://..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>"Adhérer au Parti" / Inscription (Lien)</Label>
            <Input 
              value={profile.adhesionUrl || ""} 
              onChange={e => handleChange('adhesionUrl', e.target.value)} 
              placeholder="https://..."
              type="url"
            />
          </div>
        </CardContent>
      </Card>

      {/* 4. Programme et Présentation */}
      <Card className="border-border shadow-sm">
        <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Documents & Programme</h3>
        </div>
        <CardContent className="p-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Présentation du Parti (Lien externe)</Label>
            <Input 
              value={profile.presentationUrl || ""} 
              onChange={e => handleChange('presentationUrl', e.target.value)} 
              placeholder="Lien vers la présentation..."
              type="url"
            />
          </div>
          <div className="space-y-2">
            <Label>Programme Électoral (Lien externe / PDF)</Label>
            <Input 
              value={profile.programUrl || ""} 
              onChange={e => handleChange('programUrl', e.target.value)} 
              placeholder="Lien vers le programme..."
              type="url"
            />
          </div>
        </CardContent>
      </Card>

      {/* 5. Principes et Propositions */}
      <Card className="border-border shadow-sm">
        <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-lg">Principes et Propositions</h3>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addPrinciple} className="gap-2">
            <Plus className="w-4 h-4" /> Ajouter un principe
          </Button>
        </div>
        <CardContent className="p-6 space-y-6">
          {(!profile.partyPrinciples || profile.partyPrinciples.length === 0) ? (
            <p className="text-center text-muted-foreground py-8 border-2 border-dashed rounded-xl">
              Aucun principe ou proposition ajouté pour le moment.
            </p>
          ) : (
            profile.partyPrinciples.map((principle, index) => (
              <div key={index} className="p-4 bg-muted/30 border border-border/50 rounded-xl relative group">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="absolute top-2 right-2 text-red-500 opacity-50 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                  onClick={() => removePrinciple(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div className="space-y-2">
                    <Label>Titre (Mot-clé)</Label>
                    <Input 
                      value={principle.title} 
                      onChange={e => handlePrincipleChange(index, 'title', e.target.value)} 
                      placeholder="Ex: Libéralisme économique"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lien "En savoir plus" (Optionnel)</Label>
                    <Input 
                      value={principle.readMoreUrl || ""} 
                      onChange={e => handlePrincipleChange(index, 'readMoreUrl', e.target.value)} 
                      placeholder="https://..."
                      type="url"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Description brève</Label>
                    <Textarea 
                      value={principle.description} 
                      onChange={e => handlePrincipleChange(index, 'description', e.target.value)} 
                      placeholder="Décrivez brièvement cette proposition..."
                      className="resize-none"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end sticky bottom-4 z-10 gap-4">
        <Button 
          type="button" 
          size="lg" 
          variant="outline" 
          className="gap-2 bg-background shadow-xl" 
          disabled={isSaving}
          onClick={() => handleSave(false)}
        >
          {isSaving ? <span className="animate-spin">⟳</span> : <Save className="w-5 h-5" />}
          Enregistrer (Brouillon)
        </Button>
        <Button 
          type="button" 
          size="lg" 
          className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl" 
          disabled={isSaving}
          onClick={() => handleSave(true)}
        >
          {isSaving ? <span className="animate-spin">⟳</span> : <Save className="w-5 h-5" />}
          Enregistrer & Publier
        </Button>
      </div>

    </form>
  );
}
